// src/components/banks/Table/BanksTableRow.jsx
import React from "react";
import { TableRow, TableCell, Checkbox, IconButton, Chip, Stack, Avatar, Typography } from "@mui/material";
import { MoreVert as MoreVertIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';

const BanksTableRow = ({
    bank,
    index,
    isDark,
    page,
    perPage,
    sortBy,
    sortDir,
    totalItems,
    selectedIds,
    onRowCheckboxClick,
    onMenuOpen,
    isArchive,
    menuDisabled
}) => {
    const { i18n } = useTranslation();
    const isArabic = i18n.language === "ar";
    const isSelected = selectedIds.includes(bank.id);

    const serial =
        sortDir === "asc"
            ? (page - 1) * perPage + index + 1
            : (page - 1) * perPage + (totalItems - index);

    const isOdd = index % 2 === 0;

    const bankName = bank.bank_name?.[i18n.language] || bank.bank_name?.en || bank.bank_name?.ar || "";
    const companyName = bank.company_name?.[i18n.language] || bank.company_name?.en || bank.company_name?.ar || "";
    const currencyName = bank.currency?.name?.[i18n.language] || bank.currency?.name?.en || bank.currency?.code || "-";
    const imageUrl = bank.image?.preview || bank.image?.url;

    return (
        <TableRow
            hover
            selected={isSelected}
            sx={{
                backgroundColor: isOdd
                    ? isDark
                        ? "rgba(55,65,81,0.45)"
                        : "rgba(249,250,251,0.9)"
                    : "transparent",
                opacity: isArchive ? 0.7 : 1,
            }}
        >
            <TableCell padding="checkbox">
                <Checkbox
                    color="primary"
                    checked={isSelected}
                    onChange={(e) => onRowCheckboxClick(e, bank.id)}
                    inputProps={{ "aria-labelledby": `bank-${bank?.id}` }}
                />
            </TableCell>

            <TableCell align="center">
                <IconButton
                    size="small"
                    disabled={menuDisabled}
                    onClick={(e) => {
                        e.stopPropagation();
                        onMenuOpen(e, bank);
                    }}
                    sx={{ color: isDark ? "#E5E7EB" : "#1F2937" }}
                >
                    <MoreVertIcon fontSize="small" />
                </IconButton>
            </TableCell>
            
            <TableCell>{serial}</TableCell>

            <TableCell>
                <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar
                        src={imageUrl}
                        alt={bankName}
                        variant="rounded"
                        sx={{ width: 40, height: 40, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}
                    >
                        {!imageUrl && <AccountBalanceIcon color="action" />}
                    </Avatar>
                    <Typography variant="body2" fontWeight="500">
                        {bankName}
                    </Typography>
                </Stack>
            </TableCell>

            <TableCell>{companyName}</TableCell>

            <TableCell>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {bank.account_number || "-"}
                </Typography>
            </TableCell>

            <TableCell>
                <Chip
                    label={currencyName}
                    size="small"
                    color="default"
                    variant="outlined"
                />
            </TableCell>

            <TableCell>
                <Chip
                    label={bank.is_active ? (isArabic ? "نشط" : "Active") : (isArabic ? "معطل" : "Disabled")}
                    color={bank.is_active ? "success" : "error"}
                    size="small"
                    variant={isDark ? "outlined" : "filled"}
                    sx={{
                        bgcolor: bank.is_active
                            ? (isDark ? "transparent" : "success.light")
                            : (isDark ? "transparent" : "error.light"),
                        color: bank.is_active
                            ? (isDark ? "success.main" : "success.dark")
                            : (isDark ? "error.main" : "error.dark"),
                        borderColor: bank.is_active ? "success.main" : "error.main",
                        fontWeight: "bold",
                    }}
                />
            </TableCell>

            <TableCell align="center">
                <IconButton
                    size="small"
                    disabled={menuDisabled}
                    onClick={(e) => {
                        e.stopPropagation();
                        onMenuOpen(e, bank);
                    }}
                    sx={{ color: isDark ? "#E5E7EB" : "#1F2937" }}
                >
                    <MoreVertIcon fontSize="small" />
                </IconButton>
            </TableCell>
        </TableRow>
    );
};

export default BanksTableRow;
